<div class="heading py-5">
  <h1 class="bold font-curve-large">Sizing</h1>
  <app-dependency-link
    [documentationPaths]="{ sizing: 'utilities/sizing', spacing: 'utilities/spacing' }"
  ></app-dependency-link>
</div>

<div class="alert alert-info">
  <h3 class="h5">Sizing variables are deprecated</h3>
  <!-- prettier-ignore -->
  <p>
    The current set of the post-specific sizing variables is deprecated in favour of a new naming system that is
    consistent with the Design. For further information, please read the
    <a href="https://github.com/swisspost/design-system/discussions/588">discussion on sizing variables on GitHub</a>
    and have a look at the
    <a href="https://www.figma.com/file/ojCcgC5Zd12eUSzq6V5m24/Foundations?node-id=3%3A2&t=l8qimsXlxeMLOzs6-0">implementation in Figma</a>.
  </p>
  <h4 class="h6 mt-regular">Affected properties:</h4>
  <ul>
    <li>
      <strong>margin</strong>
      and
      <strong>padding</strong>
      (new solution with updated naming system up coming)
    </li>
    <li>
      <strong>width</strong>
      ,
      <strong>height</strong>
      ,
      <strong>max-width</strong>
      and
      <strong>max-height</strong>
      (bootstrap utility remains)
    </li>
    <li>
      <strong>line-height</strong>
      (bootstrap utility remains)
    </li>
    <li>
      <strong>gap</strong>
      (new solution with updated naming system up coming)
    </li>
  </ul>
  <h4 class="h6 mt-regular">NOT affected properties:</h4>
  <ul>
    <li>
      Responsive behaviour (e.g.
      <strong>.p-large-r</strong>
      , etc.)
    </li>
  </ul>
</div>

<section>
  <h3 id="post-sizes-reference-table" class="mt-5">Post-Sizes Reference Table</h3>
  <table class="table table-striped table-sm" aria-describedby="post-sizes-reference-table">
    <thead class="thead-dark">
      <tr>
        <th>Size name</th>
        <th>Size name in classes</th>
        <th>Size in pixels (approx.)</th>
        <th>Size in rem</th>
        <th>Effective size used in css</th>
        <th>Bootstrap size equivalent</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let size of sizes">
        <td>{{ size.name | replaceHyphen | titlecase | removeSpaces }}</td>
        <td>{{ '*-' + size.name }}</td>
        <td>{{ size.size_pixel }} px</td>
        <td>{{ size.size_rem }} rem</td>
        <td>
          {{
            size.implemented === postSizeImplementationPixel
              ? size.size_pixel + 'px'
              : size.size_rem + 'rem'
          }}
        </td>
        <td>{{ size.equivalent > 0 ? '*-' + size.equivalent : 'none' }}</td>
      </tr>
    </tbody>
  </table>
</section>

<hr />

<section>
  <h3>Height & Width</h3>
  <p>
    Height and width can be set using
    <strong>h-*</strong>
    and
    <strong>w-*</strong>
    prefixes, max-height and max-width can be set using prefixes
    <strong>mh-*</strong>
    and
    <strong>mw-*</strong>
    .
  </p>

  <p>
    Bootstrap offers sizing classes with the following number suffixes:
    <strong>*-25</strong>
    ,
    <strong>*-50</strong>
    ,
    <strong>*-75</strong>
    ,
    <strong>*-100</strong>
    . They allow you to set a height / width as a percentage (see "Bootstrap Sizing" link above for
    reference).
  </p>
  <p>
    Post sizes can be used in the same way: by adding the name of the desired size to a prefix (to
    find out which size name to use, see the "Size name in classes" column in the reference table
    above or use the demo below).
  </p>

  <h4>Example</h4>
  <app-sizing-demo [sizes]="sizes"></app-sizing-demo>
</section>

<hr />

<section>
  <h3>Padding & Margin</h3>
  <p>
    Padding and margin can be set using
    <strong>p*</strong>
    and
    <strong>m*</strong>
    prefixes.
  </p>
  <p>
    You can apply the desired margin / padding all around an element using the prefix alone (
    <strong>m-*</strong>
    /
    <strong>p-*</strong>
    ), or you can specify a position (ex:
    <strong>pt-</strong>
    ,
    <strong>pb-</strong>
    ,
    <strong>pe-</strong>
    ,
    <strong>ps-</strong>
    respectively for a padding at top, bottom, right and left). You can also set a margin/padding
    along the horizontal axis (i.e. right and left) using
    <strong>px-</strong>
    /
    <strong>mx-</strong>
    or along the vertical axis (i.e. top and bottom) using
    <strong>py-</strong>
    /
    <strong>my-</strong>
    .
  </p>
  <p>
    Bootstrap offers spacing classes with suffixes going from 0 to 5 (
    <strong>*-0</strong>
    ,
    <strong>*-1</strong>
    ,
    <strong>*-2</strong>
    , ...) (see "Bootstrap Spacing" link above for reference).
  </p>
  <p>
    Post sizes can be used in the same way: by adding the name of the desired size to a prefix (to
    find out which size name to use, see the "Size name in classes" column in the reference table
    above or use the demo below).
  </p>

  <h4>Example</h4>
  <app-spacing-demo [sizes]="sizes"></app-spacing-demo>
</section>

<hr />

<section>
  <h3>Responsive behaviour</h3>
  <p>By default, the above classes apply to all breakpoints.</p>
  <p>
    If you need to change the size or spacing of an element based on the breakpoint, you should
    mention it in the class name using one of the following infixes:
    <strong>*-sm-*</strong>
    ,
    <strong>*-rg-*</strong>
    ,
    <strong>*-md-*</strong>
    ,
    <strong>*-lg-*</strong>
    ,
    <strong>*-xl-*</strong>
    ,
    <strong>*-xxl-*</strong>
    . When a breakpoint is specified, the size applies to that breakpoint and to all those that are
    larger.
  </p>
  <p>
    Another way to define a responsive size is to use the
    <strong>*-r</strong>
    suffix. It allows to obtain a size which updates automatically and consistently depending on the
    breakpoint, without having to specify anything manually.
  </p>
  <h4>Example</h4>
  <app-responsive-sizing-demo></app-responsive-sizing-demo>
</section>
